import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { api, mutate, uploadFile } from '../shared/api';

type Locale = 'hr' | 'en';
type View = 'boards' | 'board' | 'my-cards';
type BoardViewMode = 'kanban' | 'table';

// --- API types ---

type BoardSummary = {
    id: number; name: string; description: string | null; color: string | null;
    icon: string | null; visibility: string; status: string;
    member_count: number; card_count: number; my_role: string;
};

type Member = {
    id: number; user_id: number; role: string; name?: string; email?: string;
    joined_at?: string;
};

type LabelType = {
    id: number; board_id: number; name: string; color: string;
};

type ListType = {
    id: number; board_id: number; name: string; color: string | null;
    sort_order: number; is_done_list: boolean;
};

type CardSummary = {
    id: number; board_id: number; list_id: number | null; parent_card_id: number | null;
    title: string; description: string | null;
    assignee_user_id: number | null; assignee_label: string | null; assignee_name?: string | null;
    due_date: string | null; start_date: string | null; priority: string;
    custom_status: string | null; sort_order: number;
    completed_at: string | null; progress_done: number | null; progress_plan: number | null;
    comment_count?: number; checklist_total?: number; checklist_done?: number;
    labels?: LabelType[];
};

type ChecklistType = {
    id: number; card_id: number; title: string; sort_order: number;
    items: ChecklistItemType[];
};

type ChecklistItemType = {
    id: number; checklist_id: number; text: string; is_checked: boolean;
    sort_order: number; assignee_user_id: number | null; due_date: string | null;
};

type CommentType = {
    id: number; card_id: number; user_id: number; body: string;
    user_name?: string; created_at: string;
};

type ActivityType = {
    id: number; action: string; user_name?: string; meta?: Record<string, unknown>;
    created_at: string;
};

type CardDetail = {
    card: CardSummary & Record<string, unknown>;
    activity: ActivityType[];
    comments?: CommentType[];
    checklists?: ChecklistType[];
    followers?: { user_id: number; user_name?: string }[];
};

type BoardDetail = {
    board: BoardSummary & Record<string, unknown>;
    lists: ListType[];
    cards: CardSummary[];
    labels: LabelType[];
    members: Member[];
    all_users: { id: number; name: string; email: string }[];
    my_role: string;
};

type MyCardsResp = {
    overdue: CardSummary[];
    today: CardSummary[];
    upcoming: CardSummary[];
    later: CardSummary[];
    no_date: CardSummary[];
};

type NotifType = {
    id: number; type: string; card_id: number | null; board_id: number | null;
    actor_name: string | null; data: Record<string, unknown> | null;
    read_at: string | null; created_at: string;
};

// --- Style constants ---

const ink = 'var(--ad-ink, #172033)';
const gold = 'var(--ad-acc, #9b6b16)';
const field: React.CSSProperties = {
    padding: '10px 12px', border: '1px solid #cfd6df', borderRadius: 9,
    width: '100%', boxSizing: 'border-box', background: '#fff', color: ink,
    fontSize: 14, minHeight: 40,
};
const btn = (fill = true): React.CSSProperties => ({
    padding: '9px 15px', border: `1px solid ${fill ? '#172033' : '#cfd6df'}`, borderRadius: 9,
    background: fill ? '#172033' : '#fff', color: fill ? '#fff' : '#28364d',
    cursor: 'pointer', fontWeight: 650, fontSize: 13,
});
const pillBtn = (active: boolean): React.CSSProperties => ({
    padding: '6px 16px', background: active ? '#172033' : 'transparent',
    color: active ? '#fff' : '#536176', border: '1px solid ' + (active ? '#172033' : '#cfd6df'),
    borderRadius: 18, cursor: 'pointer', fontSize: '0.84rem', fontWeight: 700,
});

const PRIORITY_COLORS: Record<string, string> = {
    urgent: '#c0392b', high: '#e67e22', normal: '#7f8c8d', low: '#b6bec7',
};
const PRIORITY_LABELS: Record<string, [string, string]> = {
    urgent: ['Hitan', 'Urgent'], high: ['Visok', 'High'], normal: ['Normalan', 'Normal'], low: ['Nizak', 'Low'],
};
const PRIORITY_WEIGHT: Record<string, number> = { urgent: 0, high: 1, normal: 2, low: 3 };

const AVATAR_BG = ['#5b7db1', '#7a6bae', '#5d9a8a', '#a8846b', '#8a7f9c', '#6b93a8'];
const avatarColor = (name: string) => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return AVATAR_BG[h % AVATAR_BG.length];
};
const initialsOf = (name?: string | null) => {
    if (!name) return '';
    return name.trim().split(/\s+/).slice(0, 2).map((w) => (w[0] || '').toUpperCase()).join('');
};
const daysSince = (iso: unknown): number | null => {
    if (typeof iso !== 'string' || !iso) return null;
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return null;
    return Math.floor((Date.now() - ts) / 86400000);
};
const dueTone = (d: string | null, completed: boolean): 'overdue' | 'today' | 'soon' | 'none' => {
    if (!d || completed) return 'none';
    const due = new Date(d);
    const now = new Date();
    if (due.toDateString() === now.toDateString()) return 'today';
    due.setHours(23, 59, 59, 999);
    if (due.getTime() < now.getTime()) return 'overdue';
    if (due.getTime() - now.getTime() < 3 * 86400000) return 'soon';
    return 'none';
};
const DUE_TONE_STYLE: Record<string, { bg: string; fg: string }> = {
    overdue: { bg: '#fdecea', fg: '#c0392b' },
    today: { bg: '#fdf3e6', fg: '#b3641a' },
    soon: { bg: '#eef3f9', fg: '#2c5c8f' },
    none: { bg: '#eef1f5', fg: '#68758a' },
};

const IconBell = ({ size = 18 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
);
const IconComment = ({ size = 13 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
);
const IconCheck = ({ size = 13 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
const IconClock = ({ size = 12 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
);
const IconClose = ({ size = 16 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);
const IconBoard = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" />
    </svg>
);
const IconTable = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" />
    </svg>
);

const Avatar = ({ name, size = 22 }: { name?: string | null; size?: number }) => {
    const ini = initialsOf(name);
    if (!ini) return null;
    return (
        <span title={name || ''} style={{
            width: size, height: size, borderRadius: '50%', background: avatarColor(name || ''),
            color: '#fff', fontSize: size * 0.42, fontWeight: 700, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0, letterSpacing: 0.3,
        }}>{ini}</span>
    );
};

// --- Main module ---

export function Tasks2Module({
    token,
    locale,
    onBack,
    onOpenTeamTasks,
    onOpenAsana,
    embedded,
}: {
    token: string;
    locale: Locale;
    onBack: () => void;
    onOpenTeamTasks?: () => void;
    onOpenAsana?: () => void;
    embedded?: boolean;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [view, setView] = useState<View>('boards');
    const [boards, setBoards] = useState<BoardSummary[]>([]);
    const [boardDetail, setBoardDetail] = useState<BoardDetail | null>(null);
    const [cardDetail, setCardDetail] = useState<CardDetail | null>(null);
    const [myCards, setMyCards] = useState<MyCardsResp | null>(null);
    const [notifications, setNotifications] = useState<NotifType[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 800 : false);

    // New board form
    const [showNewBoard, setShowNewBoard] = useState(false);
    const [newBoardName, setNewBoardName] = useState('');
    const [newBoardDesc, setNewBoardDesc] = useState('');
    const [newBoardVisibility, setNewBoardVisibility] = useState<'private' | 'internal'>('private');

    // New card form
    const [showNewCard, setShowNewCard] = useState<number | null>(null); // list_id
    const [newCardTitle, setNewCardTitle] = useState('');

    // Edit card
    const [cardEditing, setCardEditing] = useState(false);

    // New comment
    const [commentText, setCommentText] = useState('');

    // New list
    const [showNewList, setShowNewList] = useState(false);
    const [newListName, setNewListName] = useState('');

    // Members panel
    const [showMembers, setShowMembers] = useState(false);
    const [addMemberUserId, setAddMemberUserId] = useState('');
    const [addMemberRole, setAddMemberRole] = useState('member');

    // Label form
    const [showLabelForm, setShowLabelForm] = useState(false);
    const [newLabelName, setNewLabelName] = useState('');
    const [newLabelColor, setNewLabelColor] = useState('#8a8a8a');

    // Board settings panel
    const [showBoardSettings, setShowBoardSettings] = useState(false);
    const [editBoardName, setEditBoardName] = useState('');
    const [editBoardDesc, setEditBoardDesc] = useState('');
    const [editBoardVisibility, setEditBoardVisibility] = useState<'private' | 'internal'>('private');
    const [editSlackWebhook, setEditSlackWebhook] = useState('');
    const [editEmailSlug, setEditEmailSlug] = useState('');

    // List rename
    const [editingListId, setEditingListId] = useState<number | null>(null);
    const [editListName, setEditListName] = useState('');

    // Push notifications
    const [pushEnabled, setPushEnabled] = useState(false);

    // Asana
    const [asanaProjectGid, setAsanaProjectGid] = useState('');

    // Drag-and-drop
    const [dragCardId, setDragCardId] = useState<number | null>(null);
    const [dragOverListId, setDragOverListId] = useState<number | null>(null);

    // Board view mode + filters
    const [boardViewMode, setBoardViewMode] = useState<BoardViewMode>('kanban');
    const [filterText, setFilterText] = useState('');
    const [filterMine, setFilterMine] = useState(false);
    const [filterPriority, setFilterPriority] = useState('');
    const [hideDone, setHideDone] = useState(false);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 800);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // --- Data loading ---

    const loadBoards = useCallback(async () => {
        try {
            const r = await api<{ boards: BoardSummary[] }>('/tasks2/boards', token);
            setBoards(r.boards);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    }, [token]);

    const loadBoard = useCallback(async (id: number) => {
        setLoading(true);
        try {
            const r = await api<BoardDetail>(`/tasks2/boards/${id}`, token);
            setBoardDetail(r);
            setView('board');
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
        setLoading(false);
    }, [token]);

    const loadCard = useCallback(async (id: number) => {
        try {
            const r = await api<CardDetail>(`/tasks2/cards/${id}`, token);
            setCardDetail(r);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    }, [token]);

    const loadMyCards = useCallback(async () => {
        try {
            const r = await api<MyCardsResp>('/tasks2/my-cards', token);
            setMyCards(r);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    }, [token]);

    const loadNotifications = useCallback(async () => {
        try {
            const r = await api<{ notifications: NotifType[]; unread_count: number }>('/tasks2/notifications', token);
            setNotifications(r.notifications);
            setUnreadCount(r.unread_count);
        } catch { /* silent */ }
    }, [token]);

    useEffect(() => { void loadBoards(); void loadNotifications(); }, [loadBoards, loadNotifications]);

    // "Moje" filter needs the set of my card ids; my-cards is an already existing endpoint.
    useEffect(() => {
        if (view === 'board' && !myCards) void loadMyCards();
    }, [view, myCards, loadMyCards]);

    // Novi board = cisti filteri, da korisnik ne gleda prazan board zbog starih filtera.
    const openBoardId = boardDetail?.board.id ?? null;
    useEffect(() => {
        setFilterText(''); setFilterMine(false); setFilterPriority(''); setHideDone(false);
    }, [openBoardId]);

    const myCardIds = useMemo(() => {
        const s = new Set<number>();
        if (myCards) {
            (Object.values(myCards) as CardSummary[][]).forEach((arr) => arr.forEach((c) => s.add(c.id)));
        }
        return s;
    }, [myCards]);

    const cardPassesFilters = useCallback((c: CardSummary): boolean => {
        if (hideDone && c.completed_at) return false;
        if (filterMine && !myCardIds.has(c.id)) return false;
        if (filterPriority && c.priority !== filterPriority) return false;
        const q = filterText.trim().toLowerCase();
        if (q) {
            const hay = `${c.title} ${c.description ?? ''} ${c.assignee_name ?? ''}`.toLowerCase();
            if (!hay.includes(q)) return false;
        }
        return true;
    }, [hideDone, filterMine, filterPriority, filterText, myCardIds]);

    const hasActiveFilters = filterMine || !!filterPriority || !!filterText.trim() || hideDone;

    // --- Actions ---

    const createBoard = async () => {
        if (!newBoardName.trim()) return;
        try {
            const r = await mutate<{ ok: boolean; id: number }>('/tasks2/boards', token, 'POST', {
                name: newBoardName.trim(),
                description: newBoardDesc.trim() || undefined,
                visibility: newBoardVisibility,
            });
            setShowNewBoard(false);
            setNewBoardName('');
            setNewBoardDesc('');
            await loadBoards();
            await loadBoard(r.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const createCard = async (listId: number) => {
        if (!newCardTitle.trim() || !boardDetail) return;
        try {
            await mutate('/tasks2/cards', token, 'POST', {
                board_id: boardDetail.board.id,
                list_id: listId,
                title: newCardTitle.trim(),
            });
            setShowNewCard(null);
            setNewCardTitle('');
            await loadBoard(boardDetail.board.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const createList = async () => {
        if (!newListName.trim() || !boardDetail) return;
        try {
            await mutate(`/tasks2/boards/${boardDetail.board.id}/lists`, token, 'POST', {
                name: newListName.trim(),
            });
            setShowNewList(false);
            setNewListName('');
            await loadBoard(boardDetail.board.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const deleteList = async (listId: number) => {
        if (!boardDetail) return;
        if (!window.confirm(t('Obrisati listu?', 'Delete this list?'))) return;
        try {
            await mutate(`/tasks2/lists/${listId}`, token, 'DELETE');
            await loadBoard(boardDetail.board.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const moveCard = async (cardId: number, targetListId: number) => {
        if (!boardDetail) return;
        try {
            await mutate(`/tasks2/cards/${cardId}/move`, token, 'POST', { list_id: targetListId });
            await loadBoard(boardDetail.board.id);
            if (cardDetail?.card.id === cardId) {
                await loadCard(cardId);
            }
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const toggleComplete = async (cardId: number) => {
        try {
            await mutate(`/tasks2/cards/${cardId}/complete`, token, 'POST');
            if (boardDetail) await loadBoard(boardDetail.board.id);
            if (cardDetail?.card.id === cardId) await loadCard(cardId);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const deleteCard = async (cardId: number) => {
        if (!window.confirm(t('Obrisati karticu?', 'Delete this card?'))) return;
        try {
            await mutate(`/tasks2/cards/${cardId}`, token, 'DELETE');
            setCardDetail(null);
            if (boardDetail) await loadBoard(boardDetail.board.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const updateCard = async (cardId: number, data: Record<string, unknown>) => {
        try {
            await mutate(`/tasks2/cards/${cardId}`, token, 'PATCH', data);
            if (boardDetail) await loadBoard(boardDetail.board.id);
            await loadCard(cardId);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const addComment = async (cardId: number) => {
        if (!commentText.trim()) return;
        try {
            await mutate(`/tasks2/cards/${cardId}/comments`, token, 'POST', { body: commentText.trim() });
            setCommentText('');
            await loadCard(cardId);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const deleteComment = async (commentId: number) => {
        if (!cardDetail) return;
        try {
            await mutate(`/tasks2/comments/${commentId}`, token, 'DELETE');
            await loadCard(cardDetail.card.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const toggleFollow = async (cardId: number) => {
        try {
            await mutate(`/tasks2/cards/${cardId}/follow`, token, 'POST');
            await loadCard(cardId);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const addMember = async () => {
        if (!boardDetail || !addMemberUserId) return;
        try {
            await mutate(`/tasks2/boards/${boardDetail.board.id}/members`, token, 'POST', {
                user_id: Number(addMemberUserId),
                role: addMemberRole,
            });
            setAddMemberUserId('');
            await loadBoard(boardDetail.board.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const removeMember = async (userId: number) => {
        if (!boardDetail) return;
        if (!window.confirm(t('Ukloniti clana?', 'Remove member?'))) return;
        try {
            await mutate(`/tasks2/boards/${boardDetail.board.id}/members/${userId}`, token, 'DELETE');
            await loadBoard(boardDetail.board.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const updateMemberRole = async (userId: number, role: string) => {
        if (!boardDetail) return;
        try {
            await mutate(`/tasks2/boards/${boardDetail.board.id}/members/${userId}`, token, 'PATCH', { role });
            await loadBoard(boardDetail.board.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const createLabel = async () => {
        if (!boardDetail || !newLabelName.trim()) return;
        try {
            await mutate(`/tasks2/boards/${boardDetail.board.id}/labels`, token, 'POST', {
                name: newLabelName.trim(),
                color: newLabelColor,
            });
            setNewLabelName('');
            setShowLabelForm(false);
            await loadBoard(boardDetail.board.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const deleteLabel = async (labelId: number) => {
        if (!boardDetail) return;
        try {
            await mutate(`/tasks2/labels/${labelId}`, token, 'DELETE');
            await loadBoard(boardDetail.board.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const toggleCardLabel = async (cardId: number, labelId: number) => {
        try {
            await mutate(`/tasks2/cards/${cardId}/labels`, token, 'POST', { label_id: labelId });
            await loadCard(cardId);
            if (boardDetail) await loadBoard(boardDetail.board.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    // Checklists
    const createChecklist = async (cardId: number) => {
        try {
            await mutate(`/tasks2/cards/${cardId}/checklists`, token, 'POST', { title: 'Checklist' });
            await loadCard(cardId);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const deleteChecklist = async (clId: number) => {
        if (!cardDetail) return;
        try {
            await mutate(`/tasks2/checklists/${clId}`, token, 'DELETE');
            await loadCard(cardDetail.card.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const addChecklistItem = async (clId: number, text: string) => {
        if (!cardDetail || !text.trim()) return;
        try {
            await mutate(`/tasks2/checklists/${clId}/items`, token, 'POST', { text: text.trim() });
            await loadCard(cardDetail.card.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const toggleChecklistItem = async (itemId: number, checked: boolean) => {
        if (!cardDetail) return;
        try {
            await mutate(`/tasks2/checklist-items/${itemId}`, token, 'PATCH', { is_checked: checked });
            await loadCard(cardDetail.card.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const deleteChecklistItem = async (itemId: number) => {
        if (!cardDetail) return;
        try {
            await mutate(`/tasks2/checklist-items/${itemId}`, token, 'DELETE');
            await loadCard(cardDetail.card.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const markNotificationsRead = async () => {
        try {
            await mutate('/tasks2/notifications/read', token, 'POST', { all: true });
            setUnreadCount(0);
            await loadNotifications();
        } catch { /* silent */ }
    };

    // Board update/delete
    const updateBoard = async () => {
        if (!boardDetail || !editBoardName.trim()) return;
        // Backend (Lada, 30.08.) prihvaca samo hooks.slack.com - uhvati prije requesta radi jasnije poruke.
        const wh = editSlackWebhook.trim();
        if (wh && !wh.startsWith('https://hooks.slack.com/')) {
            setError(t('Slack webhook mora poceti s https://hooks.slack.com/', 'Slack webhook must start with https://hooks.slack.com/'));
            return;
        }
        try {
            await mutate(`/tasks2/boards/${boardDetail.board.id}`, token, 'PATCH', {
                name: editBoardName.trim(),
                description: editBoardDesc.trim() || null,
                visibility: editBoardVisibility,
                slack_webhook_url: editSlackWebhook.trim() || null,
                email_slug: editEmailSlug.trim() || null,
            });
            setShowBoardSettings(false);
            await loadBoard(boardDetail.board.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const deleteBoard = async () => {
        if (!boardDetail) return;
        if (!window.confirm(t('Obrisati board i sve kartice?', 'Delete board and all cards?'))) return;
        try {
            await mutate(`/tasks2/boards/${boardDetail.board.id}`, token, 'DELETE');
            setBoardDetail(null);
            setShowBoardSettings(false);
            setView('boards');
            await loadBoards();
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    // List rename
    const updateList = async (listId: number) => {
        if (!boardDetail || !editListName.trim()) return;
        try {
            await mutate(`/tasks2/lists/${listId}`, token, 'PATCH', { name: editListName.trim() });
            setEditingListId(null);
            setEditListName('');
            await loadBoard(boardDetail.board.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    // Open board settings
    const openBoardSettings = () => {
        if (!boardDetail) return;
        const b = boardDetail.board as Record<string, unknown>;
        setEditBoardName(boardDetail.board.name);
        setEditBoardDesc(boardDetail.board.description || '');
        setEditBoardVisibility((boardDetail.board.visibility as 'private' | 'internal') || 'private');
        setEditSlackWebhook((b.slack_webhook_url as string) || '');
        setEditEmailSlug((b.email_slug as string) || '');
        setAsanaProjectGid((b.asana_project_gid as string) || '');
        setError('');
        setShowBoardSettings(true);
    };

    // Web Push
    const subscribePush = async () => {
        try {
            const vapid = await api<{ vapid_public_key: string | null; enabled: boolean }>('/tasks2/push/vapid-key', token);
            if (!vapid.enabled || !vapid.vapid_public_key) {
                throw new Error('Push obavijesti nisu konfigurirane na serveru.');
            }
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: vapid.vapid_public_key,
            });
            const json = sub.toJSON();
            try {
                await mutate('/tasks2/push/subscribe', token, 'POST', {
                    endpoint: json.endpoint,
                    p256dh: json.keys?.p256dh,
                    auth: json.keys?.auth,
                });
            } catch (e) {
                // Backend odbio endpoint (422 allowlista) - pocisti browser subscription da ne visi.
                await sub.unsubscribe().catch(() => undefined);
                throw e;
            }
            setPushEnabled(true);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const unsubscribePush = async () => {
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
                await mutate('/tasks2/push/unsubscribe', token, 'POST', { endpoint: sub.endpoint });
                await sub.unsubscribe();
            }
            setPushEnabled(false);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    // Asana
    const asanaLink = async () => {
        if (!boardDetail || !asanaProjectGid.trim()) return;
        try {
            // connection_user_id se NE salje: backend (30.08.) vraca 403 za tudji id, bez polja uzima trenutnog korisnika.
            await mutate(`/tasks2/boards/${boardDetail.board.id}/asana/link`, token, 'POST', {
                asana_project_gid: asanaProjectGid.trim(),
            });
            await loadBoard(boardDetail.board.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const asanaUnlink = async () => {
        if (!boardDetail) return;
        if (!window.confirm(t('Prekinuti Asana vezu?', 'Unlink from Asana?'))) return;
        try {
            await mutate(`/tasks2/boards/${boardDetail.board.id}/asana/unlink`, token, 'POST');
            await loadBoard(boardDetail.board.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const asanaSync = async () => {
        if (!boardDetail) return;
        try {
            await mutate(`/tasks2/boards/${boardDetail.board.id}/asana/sync`, token, 'POST');
            await loadBoard(boardDetail.board.id);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    const asanaPushCard = async (cardId: number) => {
        try {
            await mutate(`/tasks2/cards/${cardId}/asana/push`, token, 'POST');
            await loadCard(cardId);
        } catch (e) { setError(String((e as Error)?.message ?? e)); }
    };

    // Check push subscription status
    useEffect(() => {
        if ('serviceWorker' in navigator && 'PushManager' in window) {
            navigator.serviceWorker.ready.then((reg) => {
                reg.pushManager.getSubscription().then((sub) => {
                    setPushEnabled(!!sub);
                });
            });
        }
    }, []);

    // Escape closes the card modal (and edit state with it)
    useEffect(() => {
        if (!cardDetail) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { setCardDetail(null); setCardEditing(false); setCommentText(''); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [cardDetail]);

    // --- Render helpers ---

    const priorityDot = (p: string) => (
        <span title={t(PRIORITY_LABELS[p]?.[0] ?? p, PRIORITY_LABELS[p]?.[1] ?? p)}
            style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: PRIORITY_COLORS[p] || '#aaa', marginRight: 4, flexShrink: 0 }} />
    );

    const priorityPill = (p: string) => (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 10px', borderRadius: 12,
            fontSize: 12, fontWeight: 700, background: '#f1f3f6', color: PRIORITY_COLORS[p] || '#68758a',
            border: `1px solid ${PRIORITY_COLORS[p] || '#cfd6df'}22`,
        }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: PRIORITY_COLORS[p] || '#aaa' }} />
            {t(PRIORITY_LABELS[p]?.[0] ?? p, PRIORITY_LABELS[p]?.[1] ?? p)}
        </span>
    );

    const fmtDate = (d: string | null) => {
        if (!d) return '-';
        const dt = new Date(d);
        return `${dt.getDate()}.${dt.getMonth() + 1}.${dt.getFullYear()}`;
    };

    const duePill = (c: CardSummary, compact = false) => {
        if (!c.due_date) return null;
        const tone = dueTone(c.due_date, !!c.completed_at);
        const ts = DUE_TONE_STYLE[tone];
        const label = tone === 'today' ? t('danas', 'today') : fmtDate(c.due_date);
        return (
            <span title={`${t('Rok', 'Due')}: ${fmtDate(c.due_date)}`} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: compact ? '1px 7px' : '2px 9px', borderRadius: 10,
                fontSize: compact ? 11 : 12, fontWeight: tone === 'overdue' || tone === 'today' ? 700 : 600,
                background: ts.bg, color: ts.fg, whiteSpace: 'nowrap',
            }}>
                <IconClock size={compact ? 10 : 12} />{label}
            </span>
        );
    };

    // Aging: how long a card sits without movement (updated_at when the API provides it)
    const agingBadge = (c: CardSummary) => {
        if (c.completed_at) return null;
        const d = daysSince((c as Record<string, unknown>).updated_at ?? (c as Record<string, unknown>).created_at);
        if (d === null || d < 7) return null;
        return (
            <span title={t(`Bez pomaka ${d} dana`, `No movement for ${d} days`)} style={{
                fontSize: 11, fontWeight: 700, color: d >= 21 ? '#c0392b' : '#8a6d1a',
                background: d >= 21 ? '#fdecea' : '#faf3dd', padding: '1px 7px', borderRadius: 10, whiteSpace: 'nowrap',
            }}>{d}d</span>
        );
    };

    // --- Card detail modal ---

    const renderCardDetail = () => {
        if (!cardDetail) return null;
        const c = cardDetail.card;
        const lists = boardDetail?.lists ?? [];
        const allUsers = boardDetail?.all_users ?? [];
        const boardLabels = boardDetail?.labels ?? [];
        const closeModal = () => { setCardDetail(null); setCardEditing(false); setCommentText(''); };
        const hasAsana = !!(boardDetail && (boardDetail.board as Record<string, unknown>).asana_project_gid);

        const sectionTitle = (label: string) => (
            <div style={{
                fontSize: 11, fontWeight: 700, color: '#8a95a5', textTransform: 'uppercase',
                letterSpacing: 0.8, margin: '0 0 8px',
            }}>{label}</div>
        );

        const sidebar = (
            <div style={{
                display: 'flex', flexDirection: 'column', gap: 18,
                padding: isMobile ? '16px 20px' : '20px 20px 24px',
                background: '#f8fafc', borderLeft: isMobile ? 'none' : '1px solid #e8edf2',
                borderTop: isMobile ? '1px solid #e8edf2' : 'none',
                borderRadius: isMobile ? 0 : '0 14px 14px 0', minWidth: 0,
            }}>
                {/* List (status) */}
                {lists.length > 0 ? (
                    <div>
                        {sectionTitle(t('Lista', 'List'))}
                        <select value={c.list_id ?? ''} style={{ ...field, minHeight: 36, padding: '6px 10px' }}
                            onChange={(e) => { const v = e.target.value; if (v && Number(v) !== c.list_id) void moveCard(c.id, Number(v)); }}>
                            <option value="">{t('Bez liste', 'No list')}</option>
                            {lists.map((lst) => <option key={lst.id} value={lst.id}>{lst.name}</option>)}
                        </select>
                    </div>
                ) : null}

                {/* People + dates */}
                <div>
                    {sectionTitle(t('Detalji', 'Details'))}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: '#3d4f65' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {c.assignee_name ? <Avatar name={c.assignee_name} /> : null}
                            <span style={{ color: c.assignee_name || c.assignee_label ? '#3d4f65' : '#8a95a5' }}>
                                {c.assignee_name || c.assignee_label || t('Nedodijeljeno', 'Unassigned')}
                            </span>
                        </div>
                        <div>{priorityPill(c.priority)}</div>
                        {c.start_date ? <div><span style={{ color: '#8a95a5' }}>{t('Pocetak', 'Start')}:</span> {fmtDate(c.start_date)}</div> : null}
                        {c.due_date ? <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ color: '#8a95a5' }}>{t('Rok', 'Due')}:</span> {duePill(c)}</div> : null}
                        {agingBadge(c) ? <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ color: '#8a95a5' }}>{t('Stoji', 'Idle')}:</span> {agingBadge(c)}</div> : null}
                    </div>
                </div>

                {/* Labels */}
                {boardLabels.length > 0 ? (
                    <div>
                        {sectionTitle(t('Oznake', 'Labels'))}
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {boardLabels.map((lb) => {
                                const attached = c.labels?.some((cl) => cl.id === lb.id) ?? false;
                                return (
                                    <button key={lb.id} type="button"
                                        onClick={() => toggleCardLabel(c.id, lb.id)}
                                        title={attached ? t('Ukloni oznaku', 'Remove label') : t('Dodaj oznaku', 'Add label')}
                                        style={{
                                            padding: '3px 11px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                                            background: attached ? lb.color : '#e8edf2',
                                            color: attached ? '#fff' : '#536176',
                                            border: 'none', cursor: 'pointer', transition: 'transform .1s',
                                        }}>
                                        {lb.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ) : null}

                {/* Followers */}
                {cardDetail.followers && cardDetail.followers.length > 0 ? (
                    <div>
                        {sectionTitle(t('Prate', 'Following'))}
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                            {cardDetail.followers.map((f) => (
                                <span key={f.user_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#536176', background: '#eef1f5', borderRadius: 12, padding: '2px 9px 2px 3px' }}>
                                    <Avatar name={f.user_name || `#${f.user_id}`} size={18} />
                                    {f.user_name || `#${f.user_id}`}
                                </span>
                            ))}
                        </div>
                    </div>
                ) : null}

                {/* Actions */}
                <div>
                    {sectionTitle(t('Akcije', 'Actions'))}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <button type="button" style={{ ...btn(false), textAlign: 'left', background: '#fff' }} onClick={() => setCardEditing(!cardEditing)}>
                            {cardEditing ? t('Odustani od uredivanja', 'Cancel editing') : t('Uredi karticu', 'Edit card')}
                        </button>
                        <button type="button" style={{ ...btn(false), textAlign: 'left', background: '#fff' }} onClick={() => toggleFollow(c.id)}>
                            {t('Prati / prestani pratiti', 'Follow / unfollow')}
                        </button>
                        {hasAsana ? (
                            <button type="button" style={{ ...btn(false), textAlign: 'left', background: '#fff' }} onClick={() => void asanaPushCard(c.id)}>
                                {t('Posalji u Asanu', 'Push to Asana')}
                            </button>
                        ) : null}
                        <button type="button" style={{ ...btn(false), textAlign: 'left', background: '#fff', color: '#c0392b', borderColor: '#e5b6b0' }}
                            onClick={() => deleteCard(c.id)}>
                            {t('Obrisi karticu', 'Delete card')}
                        </button>
                    </div>
                </div>
            </div>
        );

        return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 5000, display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center' }}>
                <div className="t2-overlay" style={{ position: 'absolute', inset: 0, background: 'rgba(15,22,36,0.5)' }} onClick={closeModal} />
                <div className="t2-modal" style={{
                    position: 'relative', background: '#fff',
                    borderRadius: isMobile ? 0 : 14,
                    width: isMobile ? '100vw' : 'min(920px, 94vw)',
                    maxHeight: isMobile ? '100vh' : '92vh', height: isMobile ? '100vh' : 'auto',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                    boxShadow: '0 24px 64px rgba(0,0,0,.22)',
                }}>
                    {/* Sticky header */}
                    <div style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12,
                        padding: isMobile ? '14px 16px' : '18px 24px',
                        borderBottom: '1px solid #e8edf2', flexShrink: 0, background: '#fff',
                    }}>
                        <button type="button" onClick={() => toggleComplete(c.id)}
                            title={c.completed_at ? t('Ponovno otvori', 'Reopen') : t('Oznaci zavrseno', 'Mark complete')}
                            style={{
                                width: 28, height: 28, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                                border: `2px solid ${c.completed_at ? '#27ae60' : '#cfd6df'}`,
                                background: c.completed_at ? '#27ae60' : '#fff',
                                color: c.completed_at ? '#fff' : '#b6bec7',
                                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'background .15s, border-color .15s',
                            }}>
                            <IconCheck size={14} />
                        </button>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <h2 style={{
                                margin: 0, fontSize: 18, fontWeight: 700, color: ink, lineHeight: 1.3,
                                textDecoration: c.completed_at ? 'line-through' : 'none',
                                opacity: c.completed_at ? 0.65 : 1, overflowWrap: 'break-word',
                            }}>{c.title}</h2>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6, alignItems: 'center' }}>
                                {c.completed_at ? (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#27ae60', fontSize: 12, fontWeight: 700, background: '#eaf7ef', padding: '2px 9px', borderRadius: 10 }}>
                                        <IconCheck size={11} />{t('Zavrseno', 'Completed')}
                                    </span>
                                ) : null}
                                {(c.labels ?? []).map((lb) => (
                                    <span key={lb.id} style={{ padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: lb.color, color: '#fff' }}>{lb.name}</span>
                                ))}
                                {duePill(c)}
                            </div>
                        </div>
                        <button type="button" onClick={closeModal} title="Esc"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8a95a5', padding: 6, borderRadius: 8, flexShrink: 0 }}>
                            <IconClose />
                        </button>
                    </div>

                    {/* Body: main + sidebar */}
                    <div style={{
                        display: isMobile ? 'flex' : 'grid',
                        gridTemplateColumns: isMobile ? undefined : 'minmax(0, 1fr) 264px',
                        flexDirection: isMobile ? 'column' : undefined,
                        overflowY: 'auto', flex: 1,
                    }}>
                        {/* Main column */}
                        <div style={{ padding: isMobile ? '16px 16px 24px' : '20px 24px 28px', minWidth: 0 }}>
                            {cardEditing ? (
                                <div style={{ marginBottom: 20 }}>
                                    {sectionTitle(t('Uredivanje', 'Editing'))}
                                    <CardEditForm card={c} lists={lists} allUsers={allUsers} locale={locale}
                                        onSave={(data) => { updateCard(c.id, data); setCardEditing(false); }}
                                        onCancel={() => setCardEditing(false)} />
                                </div>
                            ) : (
                                <div style={{ marginBottom: 20 }}>
                                    {sectionTitle(t('Opis', 'Description'))}
                                    {c.description ? (
                                        <p style={{ margin: 0, color: '#3d4f65', whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.55 }}>{c.description}</p>
                                    ) : (
                                        <button type="button" onClick={() => setCardEditing(true)} style={{
                                            width: '100%', textAlign: 'left', padding: '10px 12px', fontSize: 13,
                                            color: '#8a95a5', background: '#f8fafc', border: '1px dashed #cfd6df',
                                            borderRadius: 9, cursor: 'pointer',
                                        }}>
                                            {t('Dodaj opis...', 'Add a description...')}
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Checklists */}
                            <div style={{ marginBottom: 20 }}>
                                {sectionTitle(t('Checkliste', 'Checklists'))}
                                {cardDetail.checklists?.map((cl) => {
                                    const total = cl.items.length;
                                    const done = cl.items.filter((it) => it.is_checked).length;
                                    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                                    return (
                                        <div key={cl.id} style={{ marginBottom: 12, background: '#f8fafc', borderRadius: 10, padding: 12, border: '1px solid #eef1f5' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                                <span style={{ fontWeight: 700, fontSize: 14, color: ink }}>{cl.title}</span>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <span style={{ fontSize: 12, color: '#8a95a5', fontWeight: 600 }}>{done}/{total}</span>
                                                    <button type="button" title={t('Obrisi checklistu', 'Delete checklist')}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8a95a5', padding: 2 }}
                                                        onClick={() => deleteChecklist(cl.id)}><IconClose size={13} /></button>
                                                </div>
                                            </div>
                                            {total > 0 ? (
                                                <div style={{ height: 5, background: '#e4e8ed', borderRadius: 3, marginBottom: 8, overflow: 'hidden' }}>
                                                    <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#27ae60' : '#172033', borderRadius: 3, transition: 'width .25s ease' }} />
                                                </div>
                                            ) : null}
                                            {cl.items.map((it) => (
                                                <div key={it.id} className="t2-clitem" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 4px', borderRadius: 6 }}>
                                                    <input type="checkbox" checked={it.is_checked} style={{ cursor: 'pointer', accentColor: '#27ae60' }}
                                                        onChange={(e) => toggleChecklistItem(it.id, e.target.checked)} />
                                                    <span style={{ flex: 1, textDecoration: it.is_checked ? 'line-through' : 'none', color: it.is_checked ? '#95a5a6' : ink, fontSize: 14 }}>
                                                        {it.text}
                                                    </span>
                                                    <button type="button" className="t2-clitem-x" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b6bec7', padding: 2 }}
                                                        onClick={() => deleteChecklistItem(it.id)}><IconClose size={11} /></button>
                                                </div>
                                            ))}
                                            <ChecklistItemAdder onAdd={(text) => addChecklistItem(cl.id, text)} locale={locale} />
                                        </div>
                                    );
                                })}
                                <button type="button" style={{ ...btn(false), fontSize: 12, padding: '6px 12px' }}
                                    onClick={() => createChecklist(c.id)}>
                                    + {t('Nova checklista', 'New checklist')}
                                </button>
                            </div>

                            {/* Comments */}
                            <div style={{ marginBottom: 20 }}>
                                {sectionTitle(`${t('Komentari', 'Comments')}${(cardDetail.comments?.length ?? 0) > 0 ? ` (${cardDetail.comments?.length})` : ''}`)}
                                {(cardDetail.comments ?? []).map((cm) => (
                                    <div key={cm.id} style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                                        <Avatar name={cm.user_name || `#${cm.user_id}`} size={28} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 2 }}>
                                                <span style={{ fontWeight: 600, fontSize: 13, color: ink }}>{cm.user_name || `#${cm.user_id}`}</span>
                                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                    <span style={{ fontSize: 12, color: '#8a95a5' }}>{fmtDate(cm.created_at)}</span>
                                                    <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b6bec7', padding: 2 }}
                                                        onClick={() => deleteComment(cm.id)}><IconClose size={11} /></button>
                                                </div>
                                            </div>
                                            <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', background: '#f8fafc', borderRadius: '2px 10px 10px 10px', padding: '8px 12px', color: '#3d4f65' }}>{cm.body}</div>
                                        </div>
                                    </div>
                                ))}
                                <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'flex-end' }}>
                                    <textarea value={commentText} onChange={(e) => setCommentText(e.target.value)}
                                        placeholder={t('Dodaj komentar... (Ctrl+Enter salje)', 'Add comment... (Ctrl+Enter sends)')}
                                        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void addComment(c.id); }}
                                        style={{ ...field, minHeight: 60, resize: 'vertical' }} />
                                    <button type="button" style={btn()} onClick={() => addComment(c.id)}>
                                        {t('Posalji', 'Send')}
                                    </button>
                                </div>
                            </div>

                            {/* Activity log */}
                            {cardDetail.activity.length > 0 ? (
                                <div>
                                    {sectionTitle(t('Aktivnost', 'Activity'))}
                                    <div style={{ borderLeft: '2px solid #e8edf2', paddingLeft: 12 }}>
                                        {cardDetail.activity.slice(0, 20).map((a) => (
                                            <div key={a.id} style={{ fontSize: 13, color: '#536176', padding: '3px 0' }}>
                                                <span style={{ fontWeight: 600, color: ink }}>{a.user_name || '?'}</span>{' '}
                                                {a.action}{' '}
                                                <span style={{ color: '#8a95a5' }}>{fmtDate(a.created_at)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        {sidebar}
                    </div>
                </div>
            </div>
        );
    };

    // --- Members panel ---

    const renderMembersPanel = () => {
        if (!showMembers || !boardDetail) return null;
        const bd = boardDetail;
        const myRole = bd.my_role;
        const canManage = myRole === 'owner' || myRole === 'admin';
        const nonMembers = bd.all_users.filter((u) => !bd.members.some((m) => m.user_id === u.id));

        return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 5100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }}
                    onClick={() => setShowMembers(false)} />
                <div style={{ position: 'relative', background: '#fff', borderRadius: 14, width: isMobile ? '94vw' : 480,
                    maxHeight: '80vh', overflowY: 'auto', padding: 24, boxShadow: '0 16px 48px rgba(0,0,0,.15)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{t('Clanovi', 'Members')}</h3>
                        <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}
                            onClick={() => setShowMembers(false)}>x</button>
                    </div>
                    {bd.members.map((m) => (
                        <div key={m.user_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #eee' }}>
                            <span style={{ fontSize: 14 }}>{m.name || m.email || `#${m.user_id}`}</span>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                {myRole === 'owner' ? (
                                    <select value={m.role} onChange={(e) => updateMemberRole(m.user_id, e.target.value)}
                                        style={{ fontSize: 13, padding: '2px 6px', borderRadius: 6, border: '1px solid #cfd6df' }}>
                                        <option value="viewer">viewer</option>
                                        <option value="member">member</option>
                                        <option value="admin">admin</option>
                                        <option value="owner">owner</option>
                                    </select>
                                ) : (
                                    <span style={{ fontSize: 13, color: '#8a95a5' }}>{m.role}</span>
                                )}
                                {canManage ? (
                                    <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#c0392b' }}
                                        onClick={() => removeMember(m.user_id)}>x</button>
                                ) : null}
                            </div>
                        </div>
                    ))}
                    {canManage && nonMembers.length > 0 ? (
                        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                            <select value={addMemberUserId} onChange={(e) => setAddMemberUserId(e.target.value)}
                                style={{ ...field, flex: 1 }}>
                                <option value="">{t('Odaberi korisnika', 'Select user')}</option>
                                {nonMembers.map((u) => (
                                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                                ))}
                            </select>
                            <select value={addMemberRole} onChange={(e) => setAddMemberRole(e.target.value)}
                                style={{ ...field, width: 100, flex: 'none' }}>
                                <option value="viewer">viewer</option>
                                <option value="member">member</option>
                                <option value="admin">admin</option>
                            </select>
                            <button type="button" style={btn()} onClick={addMember}>{t('Dodaj', 'Add')}</button>
                        </div>
                    ) : null}
                </div>
            </div>
        );
    };

    // --- Label management panel in board header ---

    const renderLabelForm = () => {
        if (!showLabelForm || !boardDetail) return null;
        return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 5100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }}
                    onClick={() => setShowLabelForm(false)} />
                <div style={{ position: 'relative', background: '#fff', borderRadius: 14, width: 400,
                    padding: 24, boxShadow: '0 16px 48px rgba(0,0,0,.15)' }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700 }}>{t('Oznake', 'Labels')}</h3>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>
                        {boardDetail.labels.map((lb) => (
                            <span key={lb.id} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                                background: lb.color, color: '#fff',
                            }}>
                                {lb.name}
                                <button type="button" style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
                                    onClick={() => deleteLabel(lb.id)}>x</button>
                            </span>
                        ))}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input value={newLabelName} onChange={(e) => setNewLabelName(e.target.value)}
                            placeholder={t('Naziv oznake', 'Label name')} style={{ ...field, flex: 1 }} />
                        <input type="color" value={newLabelColor} onChange={(e) => setNewLabelColor(e.target.value)}
                            style={{ width: 40, height: 40, border: '1px solid #cfd6df', borderRadius: 8, cursor: 'pointer', padding: 2 }} />
                        <button type="button" style={btn()} onClick={createLabel}>{t('Dodaj', 'Add')}</button>
                    </div>
                    <button type="button" style={{ ...btn(false), marginTop: 12, width: '100%' }}
                        onClick={() => setShowLabelForm(false)}>{t('Zatvori', 'Close')}</button>
                </div>
            </div>
        );
    };

    // --- Board settings panel ---

    const renderBoardSettings = () => {
        if (!showBoardSettings || !boardDetail) return null;
        const isAdmin = boardDetail.my_role === 'owner' || boardDetail.my_role === 'admin';
        const b = boardDetail.board as Record<string, unknown>;
        const hasAsana = !!(b.asana_project_gid);

        return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 5200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }}
                    onClick={() => setShowBoardSettings(false)} />
                <div style={{
                    position: 'relative', background: '#fff', borderRadius: 14, width: 520,
                    maxHeight: '90vh', overflowY: 'auto', padding: 24,
                    boxShadow: '0 16px 48px rgba(0,0,0,.15)',
                }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700 }}>
                        {t('Postavke boarda', 'Board settings')}
                    </h3>

                    {/* Basic info */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                        <label style={{ fontSize: 12, color: '#536176', fontWeight: 600 }}>{t('Naziv', 'Name')}</label>
                        <input value={editBoardName} onChange={(e) => setEditBoardName(e.target.value)}
                            style={field} disabled={!isAdmin} />
                        <label style={{ fontSize: 12, color: '#536176', fontWeight: 600 }}>{t('Opis', 'Description')}</label>
                        <textarea value={editBoardDesc} onChange={(e) => setEditBoardDesc(e.target.value)}
                            style={{ ...field, minHeight: 60, resize: 'vertical' }} disabled={!isAdmin} />
                        <label style={{ fontSize: 12, color: '#536176', fontWeight: 600 }}>{t('Vidljivost', 'Visibility')}</label>
                        <select value={editBoardVisibility} onChange={(e) => setEditBoardVisibility(e.target.value as 'private' | 'internal')}
                            style={field} disabled={!isAdmin}>
                            <option value="private">{t('Privatno', 'Private')}</option>
                            <option value="internal">{t('Interno', 'Internal')}</option>
                        </select>
                    </div>

                    {/* Slack webhook */}
                    {isAdmin ? (
                        <div style={{ borderTop: '1px solid #e8edf2', paddingTop: 14, marginBottom: 14 }}>
                            <label style={{ fontSize: 13, fontWeight: 700, color: ink, display: 'block', marginBottom: 6 }}>
                                Slack webhook
                            </label>
                            <input value={editSlackWebhook} onChange={(e) => setEditSlackWebhook(e.target.value)}
                                placeholder="https://hooks.slack.com/services/..."
                                style={field} />
                            <p style={{ fontSize: 12, color: '#8a95a5', margin: '4px 0 0' }}>
                                {t('Obavijesti o novim karticama, komentarima i zavrsetkima.', 'Notifications about new cards, comments and completions.')}
                            </p>
                        </div>
                    ) : null}

                    {/* Email slug */}
                    {isAdmin ? (
                        <div style={{ borderTop: '1px solid #e8edf2', paddingTop: 14, marginBottom: 14 }}>
                            <label style={{ fontSize: 13, fontWeight: 700, color: ink, display: 'block', marginBottom: 6 }}>
                                {t('Email slug (za dolaznu postu)', 'Email slug (for inbound mail)')}
                            </label>
                            <input value={editEmailSlug} onChange={(e) => setEditEmailSlug(e.target.value)}
                                placeholder="my-board"
                                style={field} />
                            <p style={{ fontSize: 12, color: '#8a95a5', margin: '4px 0 0' }}>
                                {t('Mailovi poslani na ovaj slug postaju kartice ili komentari.', 'Emails sent to this slug become cards or comments.')}
                            </p>
                        </div>
                    ) : null}

                    {/* Asana integration */}
                    {isAdmin ? (
                        <div style={{ borderTop: '1px solid #e8edf2', paddingTop: 14, marginBottom: 14 }}>
                            <label style={{ fontSize: 13, fontWeight: 700, color: ink, display: 'block', marginBottom: 6 }}>
                                Asana
                            </label>
                            {hasAsana ? (
                                <div>
                                    <div style={{ fontSize: 13, color: '#536176', marginBottom: 8 }}>
                                        {t('Povezan projekt', 'Linked project')}: <span style={{ fontWeight: 600 }}>{String(b.asana_project_gid)}</span>
                                        {b.asana_synced_at ? (
                                            <span style={{ marginLeft: 8, fontSize: 12, color: '#8a95a5' }}>
                                                {t('Zadnji sync', 'Last sync')}: {String(b.asana_synced_at).slice(0, 16)}
                                            </span>
                                        ) : null}
                                    </div>
                                    {b.asana_connection_user_id ? (
                                        <p style={{ fontSize: 12, color: '#8a95a5', margin: '0 0 8px' }}>
                                            {t('Sync ide preko konekcije korisnika', 'Sync runs through the connection of')}: {
                                                boardDetail.members.find((m) => m.user_id === Number(b.asana_connection_user_id))?.name
                                                ?? boardDetail.all_users.find((u) => u.id === Number(b.asana_connection_user_id))?.name
                                                ?? `#${String(b.asana_connection_user_id)}`
                                            }
                                        </p>
                                    ) : null}
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button type="button" style={btn()} onClick={() => void asanaSync()}>
                                            {t('Sync sada', 'Sync now')}
                                        </button>
                                        <button type="button" style={{ ...btn(false), color: '#c0392b', borderColor: '#c0392b' }}
                                            onClick={() => void asanaUnlink()}>
                                            {t('Prekini vezu', 'Unlink')}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                        <input value={asanaProjectGid} onChange={(e) => setAsanaProjectGid(e.target.value)}
                                            placeholder={t('Asana Project GID', 'Asana Project GID')}
                                            style={{ ...field, flex: 1 }} />
                                    </div>
                                    <p style={{ fontSize: 12, color: '#8a95a5', margin: '0 0 8px' }}>
                                        {t('Veza koristi tvoju Asana konekciju.', 'The link uses your own Asana connection.')}
                                    </p>
                                    <button type="button" style={btn()} onClick={() => void asanaLink()}>
                                        {t('Povezi s Asanom', 'Link to Asana')}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : null}

                    {/* Greska iz spremanja (422 validacija) - globalni bar je ispod overlaya, pa i ovdje */}
                    {error ? (
                        <div style={{
                            background: '#fdecea', color: '#b14040', borderRadius: 8,
                            padding: '8px 12px', fontSize: 13, marginTop: 12,
                        }}>{error}</div>
                    ) : null}

                    {/* Save + Delete */}
                    <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                        {isAdmin ? (
                            <button type="button" style={btn()} onClick={() => void updateBoard()}>
                                {t('Spremi', 'Save')}
                            </button>
                        ) : null}
                        <button type="button" style={btn(false)} onClick={() => setShowBoardSettings(false)}>
                            {t('Zatvori', 'Close')}
                        </button>
                        <div style={{ flex: 1 }} />
                        {boardDetail.my_role === 'owner' ? (
                            <button type="button" style={{ ...btn(false), color: '#c0392b', borderColor: '#c0392b' }}
                                onClick={() => void deleteBoard()}>
                                {t('Obrisi board', 'Delete board')}
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    };

    // --- Boards list view ---

    const renderBoards = () => (
        <div style={{ padding: isMobile ? 16 : 28, maxWidth: 900, margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: ink }}>{t('Moji boardovi', 'My boards')}</h2>
                <button type="button" style={btn()} onClick={() => setShowNewBoard(true)}>
                    + {t('Novi board', 'New board')}
                </button>
            </div>

            {showNewBoard ? (
                <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, border: '1px solid #e8edf2' }}>
                    <input value={newBoardName} onChange={(e) => setNewBoardName(e.target.value)}
                        placeholder={t('Naziv boarda', 'Board name')} style={{ ...field, marginBottom: 10 }}
                        onKeyDown={(e) => { if (e.key === 'Enter') void createBoard(); }} autoFocus />
                    <textarea value={newBoardDesc} onChange={(e) => setNewBoardDesc(e.target.value)}
                        placeholder={t('Opis (neobavezno)', 'Description (optional)')}
                        style={{ ...field, marginBottom: 10, minHeight: 60 }} />
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <select value={newBoardVisibility} onChange={(e) => setNewBoardVisibility(e.target.value as 'private' | 'internal')}
                            style={{ ...field, width: 160, flex: 'none' }}>
                            <option value="private">{t('Privatno', 'Private')}</option>
                            <option value="internal">{t('Interno', 'Internal')}</option>
                        </select>
                        <button type="button" style={btn()} onClick={() => void createBoard()}>
                            {t('Kreiraj', 'Create')}
                        </button>
                        <button type="button" style={btn(false)} onClick={() => { setShowNewBoard(false); setNewBoardName(''); setNewBoardDesc(''); }}>
                            {t('Odustani', 'Cancel')}
                        </button>
                    </div>
                </div>
            ) : null}

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
                {boards.map((b) => (
                    <div key={b.id} onClick={() => void loadBoard(b.id)} className="t2-boardcard"
                        style={{
                            background: '#fff', borderRadius: 12, cursor: 'pointer',
                            border: '1px solid #e8edf2', overflow: 'hidden',
                        }}>
                        <div style={{ height: 4, background: b.color || '#cfd6df' }} />
                        <div style={{ padding: '14px 18px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                <span style={{ fontWeight: 700, fontSize: 15, color: ink, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</span>
                                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', background: '#eef1f5', color: '#536176', borderRadius: 10, flexShrink: 0 }}>{b.my_role}</span>
                            </div>
                            {b.description ? (
                                <p style={{ margin: '0 0 10px', fontSize: 13, color: '#536176', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{b.description}</p>
                            ) : null}
                            <div style={{ display: 'flex', gap: 14, fontSize: 12, color: '#8a95a5', alignItems: 'center' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><IconBoard size={12} />{b.card_count} {t('kartica', 'cards')}</span>
                                <span>{b.member_count} {t('clanova', 'members')}</span>
                                {b.visibility === 'internal' ? <span style={{ marginLeft: 'auto' }}>{t('interno', 'internal')}</span> : null}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {boards.length === 0 && !showNewBoard ? (
                <div style={{ textAlign: 'center', marginTop: 60, color: '#8a95a5' }}>
                    <div style={{ display: 'inline-flex', width: 64, height: 64, borderRadius: 16, background: '#eef1f5', alignItems: 'center', justifyContent: 'center', color: '#b6bec7', marginBottom: 14 }}>
                        <IconBoard size={30} />
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#536176', marginBottom: 4 }}>{t('Jos nema boardova', 'No boards yet')}</div>
                    <div style={{ fontSize: 13, marginBottom: 16 }}>{t('Board je prostor za jedan projekt ili tim.', 'A board is the space for one project or team.')}</div>
                    <button type="button" style={btn()} onClick={() => setShowNewBoard(true)}>
                        + {t('Kreiraj prvi board', 'Create your first board')}
                    </button>
                </div>
            ) : null}
        </div>
    );

    // --- Loading skeleton (board opening) ---

    const renderSkeleton = () => (
        <div style={{ flex: 1, display: 'flex', gap: 14, padding: isMobile ? '14px 14px' : '16px 24px', alignItems: 'flex-start', overflow: 'hidden' }}>
            {[0, 1, 2].map((i) => (
                <div key={i} style={{ minWidth: 280, width: 300, background: '#f0f2f5', borderRadius: 12, padding: 12, flexShrink: 0 }}>
                    <div className="t2-skel" style={{ height: 16, width: '55%', borderRadius: 6, marginBottom: 12 }} />
                    {Array.from({ length: 3 - (i % 2) }).map((_, j) => (
                        <div key={j} style={{ background: '#fff', borderRadius: 10, padding: 12, marginBottom: 8, border: '1px solid #e4e8ed' }}>
                            <div className="t2-skel" style={{ height: 12, width: '85%', borderRadius: 5, marginBottom: 8 }} />
                            <div className="t2-skel" style={{ height: 10, width: '45%', borderRadius: 5 }} />
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );

    // --- My cards view ---

    const renderMyCards = () => {
        if (!myCards) {
            return (
                <div style={{ padding: isMobile ? 16 : 28, maxWidth: 800, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
                    <div className="t2-skel" style={{ height: 22, width: 160, borderRadius: 6, marginBottom: 20 }} />
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="t2-skel" style={{ height: 44, borderRadius: 10, marginBottom: 8 }} />
                    ))}
                </div>
            );
        }
        const sections: { key: keyof MyCardsResp; hr: string; en: string; color: string }[] = [
            { key: 'overdue', hr: 'Prekoraceno', en: 'Overdue', color: '#c0392b' },
            { key: 'today', hr: 'Danas', en: 'Today', color: '#e67e22' },
            { key: 'upcoming', hr: 'Uskoro', en: 'Upcoming', color: '#2980b9' },
            { key: 'later', hr: 'Kasnije', en: 'Later', color: '#7f8c8d' },
            { key: 'no_date', hr: 'Bez roka', en: 'No date', color: '#95a5a6' },
        ];
        const totalN = Object.values(myCards).reduce((n, arr) => n + arr.length, 0);
        return (
            <div style={{ padding: isMobile ? 16 : 28, maxWidth: 800, margin: '0 auto', width: '100%', boxSizing: 'border-box', overflowY: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 20 }}>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: ink }}>{t('Moji zadaci', 'My tasks')}</h2>
                    {totalN > 0 ? <span style={{ fontSize: 13, color: '#8a95a5', fontWeight: 600 }}>{totalN}</span> : null}
                </div>
                {sections.map((s) => {
                    const cards = myCards[s.key];
                    if (cards.length === 0) return null;
                    return (
                        <div key={s.key} style={{ marginBottom: 18, background: '#fff', borderRadius: 12, border: '1px solid #e8edf2', overflow: 'hidden' }}>
                            <div style={{ padding: '10px 16px', borderBottom: '1px solid #eef1f5', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color }} />
                                <span style={{ fontSize: 14, fontWeight: 700, color: ink }}>{t(s.hr, s.en)}</span>
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#68758a', background: '#eef1f5', borderRadius: 9, padding: '1px 8px' }}>{cards.length}</span>
                            </div>
                            {cards.map((c) => (
                                <div key={c.id} onClick={() => void loadCard(c.id)} className="t2-myrow"
                                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', cursor: 'pointer', borderBottom: '1px solid #f4f6f8' }}>
                                    <span style={{ width: 3, height: 20, borderRadius: 2, background: PRIORITY_COLORS[c.priority] || '#e4e8ed', flexShrink: 0 }} />
                                    <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: ink, textDecoration: c.completed_at ? 'line-through' : 'none', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</span>
                                    {agingBadge(c)}
                                    {duePill(c, true)}
                                </div>
                            ))}
                        </div>
                    );
                })}
                {totalN === 0 ? (
                    <div style={{ textAlign: 'center', marginTop: 60, color: '#8a95a5' }}>
                        <div style={{ display: 'inline-flex', width: 64, height: 64, borderRadius: '50%', background: '#eaf7ef', alignItems: 'center', justifyContent: 'center', color: '#27ae60', marginBottom: 14 }}>
                            <IconCheck size={28} />
                        </div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#536176', marginBottom: 4 }}>{t('Sve rijeseno', 'All clear')}</div>
                        <div style={{ fontSize: 13 }}>{t('Nemas dodijeljenih zadataka.', 'You have no assigned tasks.')}</div>
                    </div>
                ) : null}
            </div>
        );
    };

    // --- Board detail (Kanban) view ---

    const renderKanbanCard = (c: CardSummary, canEdit: boolean) => (
        <div key={c.id} onClick={() => void loadCard(c.id)}
            className={`t2-card${dragCardId === c.id ? ' t2-card-dragging' : ''}`}
            draggable={canEdit}
            onDragStart={(e) => {
                setDragCardId(c.id);
                e.dataTransfer.effectAllowed = 'move';
            }}
            onDragEnd={() => { setDragCardId(null); setDragOverListId(null); }}
            style={{
                background: '#fff', borderRadius: 10, padding: '10px 12px 10px 14px', marginBottom: 8,
                cursor: canEdit ? 'grab' : 'pointer', border: '1px solid #e4e8ed',
                borderLeft: `3px solid ${PRIORITY_COLORS[c.priority] || '#e4e8ed'}`,
                opacity: c.completed_at ? 0.62 : 1,
            }}>
            {/* Labels row */}
            {c.labels && c.labels.length > 0 ? (
                <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginBottom: 6 }}>
                    {c.labels.map((lb) => (
                        <span key={lb.id} title={lb.name} style={{
                            width: 28, height: 6, borderRadius: 3, background: lb.color, display: 'inline-block',
                        }} />
                    ))}
                </div>
            ) : null}
            <div style={{ fontSize: 14, fontWeight: 600, color: ink, marginBottom: 6, lineHeight: 1.35, textDecoration: c.completed_at ? 'line-through' : 'none', overflowWrap: 'break-word' }}>
                {c.title}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {duePill(c, true)}
                {agingBadge(c)}
                {(c.checklist_total ?? 0) > 0 ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, color: (c.checklist_done ?? 0) === c.checklist_total ? '#27ae60' : '#68758a' }}>
                        <IconCheck size={11} />{c.checklist_done ?? 0}/{c.checklist_total}
                    </span>
                ) : null}
                {(c.comment_count ?? 0) > 0 ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, color: '#68758a' }}>
                        <IconComment size={11} />{c.comment_count}
                    </span>
                ) : null}
                <span style={{ flex: 1 }} />
                {c.assignee_name ? <Avatar name={c.assignee_name} size={20} />
                    : c.assignee_label ? <span style={{ fontSize: 11, color: '#8a95a5' }}>{c.assignee_label}</span> : null}
            </div>
        </div>
    );

    const renderBoardToolbar = (bd: BoardDetail) => {
        const openCards = bd.cards.filter((c) => !c.completed_at);
        const overdueN = openCards.filter((c) => dueTone(c.due_date, false) === 'overdue').length;
        const todayN = openCards.filter((c) => dueTone(c.due_date, false) === 'today').length;
        const doneN = bd.cards.length - openCards.length;
        const staleN = openCards.filter((c) => {
            const d = daysSince((c as Record<string, unknown>).updated_at ?? (c as Record<string, unknown>).created_at);
            return d !== null && d >= 7;
        }).length;
        const visibleN = bd.cards.filter(cardPassesFilters).length;

        const statChip = (label: string, value: number, fg: string, bg: string) => (
            value > 0 ? (
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px',
                    borderRadius: 12, fontSize: 12, fontWeight: 700, background: bg, color: fg, whiteSpace: 'nowrap',
                }}>
                    <span style={{ fontSize: 13 }}>{value}</span>{label}
                </span>
            ) : null
        );

        return (
            <div style={{
                padding: isMobile ? '8px 14px' : '10px 24px', borderBottom: '1px solid #e8edf2',
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: '#fbfcfd',
            }}>
                {/* View toggle */}
                <div style={{ display: 'inline-flex', background: '#eef1f5', borderRadius: 9, padding: 3, gap: 2 }}>
                    <button type="button" onClick={() => setBoardViewMode('kanban')}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px',
                            border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                            background: boardViewMode === 'kanban' ? '#fff' : 'transparent',
                            color: boardViewMode === 'kanban' ? ink : '#68758a',
                            boxShadow: boardViewMode === 'kanban' ? '0 1px 4px rgba(23,32,51,.12)' : 'none',
                        }}>
                        <IconBoard />{t('Kanban', 'Kanban')}
                    </button>
                    <button type="button" onClick={() => setBoardViewMode('table')}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px',
                            border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                            background: boardViewMode === 'table' ? '#fff' : 'transparent',
                            color: boardViewMode === 'table' ? ink : '#68758a',
                            boxShadow: boardViewMode === 'table' ? '0 1px 4px rgba(23,32,51,.12)' : 'none',
                        }}>
                        <IconTable />{t('Tablica', 'Table')}
                    </button>
                </div>

                {/* Overview stats */}
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {statChip(t('otvoreno', 'open'), openCards.length, '#3d4f65', '#eef1f5')}
                    {statChip(t('prekoraceno', 'overdue'), overdueN, '#c0392b', '#fdecea')}
                    {statChip(t('danas', 'today'), todayN, '#b3641a', '#fdf3e6')}
                    {statChip(t('stoji 7+ dana', 'idle 7+ days'), staleN, '#8a6d1a', '#faf3dd')}
                    {statChip(t('gotovo', 'done'), doneN, '#1e8449', '#eaf7ef')}
                </div>

                <div style={{ flex: 1 }} />

                {/* Filters */}
                <input value={filterText} onChange={(e) => setFilterText(e.target.value)}
                    placeholder={t('Trazi kartice...', 'Search cards...')}
                    style={{ ...field, minHeight: 32, padding: '5px 11px', fontSize: 13, width: isMobile ? '100%' : 190, borderRadius: 8 }} />
                <button type="button" style={{ ...pillBtn(filterMine), padding: '5px 13px', fontSize: '0.78rem' }}
                    title={t('Samo kartice dodijeljene meni', 'Only cards assigned to me')}
                    onClick={() => setFilterMine(!filterMine)}>
                    {t('Moje', 'Mine')}
                </button>
                <button type="button" style={{ ...pillBtn(hideDone), padding: '5px 13px', fontSize: '0.78rem' }}
                    onClick={() => setHideDone(!hideDone)}>
                    {t('Sakrij gotove', 'Hide done')}
                </button>
                <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}
                    style={{ ...field, minHeight: 32, padding: '4px 8px', fontSize: 13, width: 130, borderRadius: 8 }}>
                    <option value="">{t('Svi prioriteti', 'All priorities')}</option>
                    {(['urgent', 'high', 'normal', 'low'] as const).map((p) => (
                        <option key={p} value={p}>{t(PRIORITY_LABELS[p][0], PRIORITY_LABELS[p][1])}</option>
                    ))}
                </select>
                {hasActiveFilters ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#536176', fontWeight: 600 }}>
                        {visibleN}/{bd.cards.length}
                        <button type="button"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: gold, fontSize: 12, fontWeight: 700, padding: 0, textDecoration: 'underline' }}
                            onClick={() => { setFilterText(''); setFilterMine(false); setFilterPriority(''); setHideDone(false); }}>
                            {t('Ocisti', 'Clear')}
                        </button>
                    </span>
                ) : null}
            </div>
        );
    };

    const renderTableView = (bd: BoardDetail) => {
        const listById = new Map(bd.lists.map((l) => [l.id, l]));
        const rows = bd.cards
            .filter(cardPassesFilters)
            .slice()
            .sort((a, b) => {
                if (!!a.completed_at !== !!b.completed_at) return a.completed_at ? 1 : -1;
                const ad = a.due_date ? new Date(a.due_date).getTime() : Infinity;
                const bdd = b.due_date ? new Date(b.due_date).getTime() : Infinity;
                if (ad !== bdd) return ad - bdd;
                const aw = PRIORITY_WEIGHT[a.priority] ?? 9;
                const bw = PRIORITY_WEIGHT[b.priority] ?? 9;
                if (aw !== bw) return aw - bw;
                return a.title.localeCompare(b.title);
            });

        const th: React.CSSProperties = {
            textAlign: 'left', padding: '9px 12px', fontSize: 11, fontWeight: 700, color: '#8a95a5',
            textTransform: 'uppercase', letterSpacing: 0.7, borderBottom: '2px solid #e4e8ed',
            position: 'sticky', top: 0, background: '#fff', zIndex: 1, whiteSpace: 'nowrap',
        };
        const td: React.CSSProperties = { padding: '9px 12px', fontSize: 13, borderBottom: '1px solid #eef1f5', verticalAlign: 'middle' };

        if (rows.length === 0) {
            return (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
                    <div style={{ textAlign: 'center', color: '#8a95a5' }}>
                        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
                            {hasActiveFilters ? t('Nista ne prolazi filtere', 'Nothing matches the filters') : t('Nema kartica', 'No cards')}
                        </div>
                        <div style={{ fontSize: 13 }}>
                            {hasActiveFilters ? t('Ocisti filtere za sve kartice.', 'Clear filters to see all cards.') : t('Dodaj prvu karticu u Kanban pogledu.', 'Add the first card in the Kanban view.')}
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div style={{ flex: 1, overflow: 'auto', padding: isMobile ? '0 0 20px' : '0 24px 24px' }}>
                <div style={{ background: '#fff', borderRadius: isMobile ? 0 : 12, border: '1px solid #e8edf2', marginTop: 16, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ ...th, width: 36 }} />
                                <th style={th}>{t('Naslov', 'Title')}</th>
                                {!isMobile ? <th style={th}>{t('Lista', 'List')}</th> : null}
                                {!isMobile ? <th style={th}>{t('Nositelj', 'Assignee')}</th> : null}
                                <th style={th}>{t('Rok', 'Due')}</th>
                                {!isMobile ? <th style={th}>{t('Prioritet', 'Priority')}</th> : null}
                                {!isMobile ? <th style={{ ...th, textAlign: 'right' }}>{t('Napredak', 'Progress')}</th> : null}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((c) => {
                                const lst = c.list_id ? listById.get(c.list_id) : undefined;
                                return (
                                    <tr key={c.id} className="t2-row" onClick={() => void loadCard(c.id)}
                                        style={{ cursor: 'pointer', opacity: c.completed_at ? 0.55 : 1 }}>
                                        <td style={{ ...td, paddingRight: 0 }}>
                                            <span style={{ display: 'inline-block', width: 4, height: 22, borderRadius: 2, background: PRIORITY_COLORS[c.priority] || '#e4e8ed' }} />
                                        </td>
                                        <td style={{ ...td, fontWeight: 600, color: ink, textDecoration: c.completed_at ? 'line-through' : 'none' }}>
                                            {c.title}
                                            {c.labels && c.labels.length > 0 ? (
                                                <span style={{ marginLeft: 8, display: 'inline-flex', gap: 3, verticalAlign: 'middle' }}>
                                                    {c.labels.map((lb) => (
                                                        <span key={lb.id} title={lb.name} style={{ width: 20, height: 6, borderRadius: 3, background: lb.color, display: 'inline-block' }} />
                                                    ))}
                                                </span>
                                            ) : null}
                                        </td>
                                        {!isMobile ? (
                                            <td style={td}>
                                                {lst ? (
                                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#536176', background: '#eef1f5', padding: '2px 9px', borderRadius: 10, whiteSpace: 'nowrap' }}>{lst.name}</span>
                                                ) : <span style={{ color: '#b6bec7' }}>-</span>}
                                            </td>
                                        ) : null}
                                        {!isMobile ? (
                                            <td style={td}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                    {c.assignee_name ? <Avatar name={c.assignee_name} size={20} /> : null}
                                                    <span style={{ color: c.assignee_name || c.assignee_label ? '#3d4f65' : '#b6bec7' }}>
                                                        {c.assignee_name || c.assignee_label || '-'}
                                                    </span>
                                                </span>
                                            </td>
                                        ) : null}
                                        <td style={td}>{duePill(c) || <span style={{ color: '#b6bec7' }}>-</span>}</td>
                                        {!isMobile ? <td style={td}>{priorityPill(c.priority)}</td> : null}
                                        {!isMobile ? (
                                            <td style={{ ...td, textAlign: 'right' }}>
                                                <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center', color: '#68758a', fontSize: 12, fontWeight: 600 }}>
                                                    {(c.checklist_total ?? 0) > 0 ? (
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                                            <IconCheck size={11} />{c.checklist_done ?? 0}/{c.checklist_total}
                                                        </span>
                                                    ) : null}
                                                    {(c.comment_count ?? 0) > 0 ? (
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                                            <IconComment size={11} />{c.comment_count}
                                                        </span>
                                                    ) : null}
                                                    {agingBadge(c)}
                                                </span>
                                            </td>
                                        ) : null}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderBoard = () => {
        if (!boardDetail) return null;
        const bd = boardDetail;
        const canEdit = bd.my_role === 'owner' || bd.my_role === 'admin' || bd.my_role === 'member';

        return (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                {/* Board header */}
                <div style={{ padding: isMobile ? '10px 14px' : '12px 24px', borderBottom: '1px solid #e8edf2', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: '#fff' }}>
                    <button type="button" style={{ ...btn(false), padding: '6px 12px' }}
                        onClick={() => { setBoardDetail(null); setView('boards'); void loadBoards(); }}>
                        {t('Nazad', 'Back')}
                    </button>
                    {bd.board.color ? <span style={{ width: 14, height: 14, borderRadius: 4, background: bd.board.color }} /> : null}
                    <span style={{ fontWeight: 700, fontSize: 17, color: ink }}>{bd.board.name}</span>
                    <span style={{ fontSize: 12, color: '#8a95a5', background: '#eef1f5', padding: '2px 9px', borderRadius: 10 }}>
                        {bd.board.visibility === 'private' ? t('privatno', 'private') : t('interno', 'internal')}
                    </span>
                    <div style={{ flex: 1 }} />
                    <button type="button" style={btn(false)} onClick={() => setShowLabelForm(true)}>
                        {t('Oznake', 'Labels')} ({bd.labels.length})
                    </button>
                    <button type="button" style={btn(false)} onClick={() => setShowMembers(true)}>
                        {t('Clanovi', 'Members')} ({bd.members.length})
                    </button>
                    {/* Postavke vidljive svima; panel sam gasi izmjene za ne-admina (disabled polja). Vedran 30.08. */}
                    <button type="button" style={btn(false)} onClick={openBoardSettings}>
                        {t('Postavke', 'Settings')}
                    </button>
                </div>

                {renderBoardToolbar(bd)}

                {boardViewMode === 'table' ? renderTableView(bd) : (
                <div className="t2-lanes-mobile" style={{
                    flex: 1, display: 'flex', overflowX: 'auto', gap: 14,
                    padding: isMobile ? '14px 14px' : '16px 24px', alignItems: 'flex-start',
                }}>
                    {bd.lists.slice().sort((a, b) => a.sort_order - b.sort_order).map((lst) => {
                        const allListCards = bd.cards.filter((c) => c.list_id === lst.id);
                        const listCards = allListCards
                            .filter(cardPassesFilters)
                            .sort((a, b) => a.sort_order - b.sort_order);
                        const isDragTarget = dragOverListId === lst.id && dragCardId !== null;
                        return (
                            <div key={lst.id} className={`t2-lane${isDragTarget ? ' t2-lane-over' : ''}`} style={{
                                minWidth: isMobile ? 260 : 280, maxWidth: 320, width: isMobile ? 260 : 300,
                                background: '#f0f2f5', borderRadius: 12, flexShrink: 0,
                                display: 'flex', flexDirection: 'column', maxHeight: '100%',
                                border: '1px solid transparent',
                            }}>
                                {/* List header */}
                                <div style={{
                                    padding: '12px 14px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6,
                                }}>
                                    {editingListId === lst.id ? (
                                        <input value={editListName}
                                            onChange={(e) => setEditListName(e.target.value)}
                                            onBlur={() => { void updateList(lst.id); }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') void updateList(lst.id);
                                                if (e.key === 'Escape') { setEditingListId(null); setEditListName(''); }
                                            }}
                                            style={{ fontWeight: 700, fontSize: 14, color: ink, border: `1px solid ${gold}`, borderRadius: 6, padding: '2px 6px', width: '100%' }}
                                            autoFocus />
                                    ) : (
                                        <span title={canEdit ? t('Dvoklik za preimenovanje', 'Double-click to rename') : undefined}
                                            style={{ fontWeight: 700, fontSize: 14, color: ink, cursor: canEdit ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}
                                            onDoubleClick={() => { if (canEdit) { setEditingListId(lst.id); setEditListName(lst.name); } }}>
                                            {lst.color ? <span style={{ width: 8, height: 8, borderRadius: '50%', background: lst.color, flexShrink: 0 }} /> : null}
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lst.name}</span>
                                            {lst.is_done_list ? <span style={{ color: '#27ae60', display: 'inline-flex' }} title={t('Lista za gotovo', 'Done list')}><IconCheck size={12} /></span> : null}
                                            <span style={{
                                                color: '#68758a', fontSize: 11, fontWeight: 700, background: '#e4e8ed',
                                                borderRadius: 9, padding: '1px 7px', flexShrink: 0,
                                            }}>{hasActiveFilters ? `${listCards.length}/${allListCards.length}` : allListCards.length}</span>
                                        </span>
                                    )}
                                    {canEdit ? (
                                        <button type="button" title={t('Obrisi listu', 'Delete list')}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8a95a5', padding: 2, flexShrink: 0 }}
                                            onClick={() => deleteList(lst.id)}><IconClose size={13} /></button>
                                    ) : null}
                                </div>

                                {/* Cards */}
                                <div style={{
                                    flex: 1, overflowY: 'auto', padding: '0 10px 10px',
                                    borderRadius: 8, minHeight: 60,
                                }}
                                onDragOver={(e) => { e.preventDefault(); if (dragOverListId !== lst.id) setDragOverListId(lst.id); }}
                                onDragLeave={(e) => {
                                    if (!(e.currentTarget as HTMLDivElement).contains(e.relatedTarget as Node)) setDragOverListId(null);
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setDragOverListId(null);
                                    if (dragCardId && lst.id !== bd.cards.find((c) => c.id === dragCardId)?.list_id) {
                                        void moveCard(dragCardId, lst.id);
                                    }
                                    setDragCardId(null);
                                }}>
                                    {listCards.map((c) => renderKanbanCard(c, canEdit))}

                                    {/* Drop hint while dragging over */}
                                    {isDragTarget ? (
                                        <div style={{
                                            border: `2px dashed ${gold}`, borderRadius: 10, padding: '14px 12px',
                                            textAlign: 'center', fontSize: 12, fontWeight: 700, color: gold,
                                            background: 'rgba(155,107,22,.06)', marginBottom: 8,
                                        }}>
                                            {t('Ispusti ovdje', 'Drop here')}
                                        </div>
                                    ) : null}

                                    {/* Empty lane state */}
                                    {listCards.length === 0 && !isDragTarget && showNewCard !== lst.id ? (
                                        <div style={{
                                            border: '1px dashed #cfd6df', borderRadius: 10, padding: '16px 12px',
                                            textAlign: 'center', fontSize: 12, color: '#8a95a5', marginBottom: 8,
                                        }}>
                                            {hasActiveFilters && allListCards.length > 0
                                                ? t('Filteri skrivaju kartice', 'Filters hide the cards')
                                                : t('Povuci karticu ovamo', 'Drag a card here')}
                                        </div>
                                    ) : null}

                                    {/* Add card */}
                                    {canEdit ? (
                                        showNewCard === lst.id ? (
                                            <div style={{ marginTop: 4, background: '#fff', borderRadius: 10, padding: 8, border: '1px solid #e4e8ed' }}>
                                                <input value={newCardTitle} onChange={(e) => setNewCardTitle(e.target.value)}
                                                    placeholder={t('Naslov kartice...', 'Card title...')}
                                                    style={{ ...field, fontSize: 13, marginBottom: 6 }} autoFocus
                                                    onKeyDown={(e) => { if (e.key === 'Enter') void createCard(lst.id); if (e.key === 'Escape') { setShowNewCard(null); setNewCardTitle(''); } }} />
                                                <div style={{ display: 'flex', gap: 6 }}>
                                                    <button type="button" style={{ ...btn(), fontSize: 12, padding: '6px 12px' }}
                                                        onClick={() => void createCard(lst.id)}>{t('Dodaj', 'Add')}</button>
                                                    <button type="button" style={{ ...btn(false), fontSize: 12, padding: '6px 12px' }}
                                                        onClick={() => { setShowNewCard(null); setNewCardTitle(''); }}>{t('Odustani', 'Cancel')}</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button type="button" className="t2-addcard"
                                                onClick={() => { setShowNewCard(lst.id); setNewCardTitle(''); }}
                                                style={{ width: '100%', padding: '8px 12px', border: 'none', background: 'transparent',
                                                    color: '#8a95a5', cursor: 'pointer', textAlign: 'left', fontSize: 13, borderRadius: 8 }}>
                                                + {t('Dodaj karticu', 'Add card')}
                                            </button>
                                        )
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}

                    {/* Unassigned cards (no list) */}
                    {(() => {
                        const orphans = bd.cards.filter((c) => !c.list_id && cardPassesFilters(c));
                        if (orphans.length === 0) return null;
                        return (
                            <div style={{
                                minWidth: isMobile ? 260 : 280, maxWidth: 320, width: isMobile ? 260 : 300,
                                background: '#fef9f0', borderRadius: 12, flexShrink: 0,
                                display: 'flex', flexDirection: 'column', maxHeight: '100%',
                            }}>
                                <div style={{ padding: '12px 14px 8px' }}>
                                    <span style={{ fontWeight: 700, fontSize: 14, color: '#9b6b16' }}>
                                        {t('Bez liste', 'No list')}
                                        <span style={{ color: '#8a95a5', fontSize: 12, fontWeight: 400, marginLeft: 6 }}>{orphans.length}</span>
                                    </span>
                                </div>
                                <div style={{ flex: 1, overflowY: 'auto', padding: '0 10px 10px' }}>
                                    {orphans.map((c) => renderKanbanCard(c, canEdit))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Add list button */}
                    {canEdit ? (
                        <div style={{ minWidth: isMobile ? 240 : 280, flexShrink: 0 }}>
                            {showNewList ? (
                                <div style={{ background: '#f0f2f5', borderRadius: 12, padding: 14 }}>
                                    <input value={newListName} onChange={(e) => setNewListName(e.target.value)}
                                        placeholder={t('Naziv liste...', 'List name...')}
                                        style={{ ...field, fontSize: 13, marginBottom: 8 }} autoFocus
                                        onKeyDown={(e) => { if (e.key === 'Enter') void createList(); if (e.key === 'Escape') { setShowNewList(false); setNewListName(''); } }} />
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        <button type="button" style={{ ...btn(), fontSize: 12, padding: '6px 12px' }}
                                            onClick={() => void createList()}>{t('Dodaj', 'Add')}</button>
                                        <button type="button" style={{ ...btn(false), fontSize: 12, padding: '6px 12px' }}
                                            onClick={() => { setShowNewList(false); setNewListName(''); }}>{t('Odustani', 'Cancel')}</button>
                                    </div>
                                </div>
                            ) : (
                                <button type="button" className="t2-addlist" onClick={() => setShowNewList(true)}
                                    style={{
                                        width: '100%', padding: '12px 14px', border: '2px dashed #cfd6df',
                                        borderRadius: 12, background: 'transparent', cursor: 'pointer',
                                        color: '#8a95a5', fontSize: 14, fontWeight: 600,
                                    }}>
                                    + {t('Nova lista', 'New list')}
                                </button>
                            )}
                        </div>
                    ) : null}
                </div>
                )}
            </div>
        );
    };

    // --- Notifications dropdown ---

    const [showNotifs, setShowNotifs] = useState(false);

    const renderNotifications = () => {
        if (!showNotifs) return null;
        return (
            <div style={{
                position: 'absolute', right: 24, top: 52, width: 360, maxHeight: 400,
                overflowY: 'auto', background: '#fff', borderRadius: 12,
                boxShadow: '0 12px 40px rgba(0,0,0,.15)', zIndex: 4200, padding: 14,
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{t('Obavijesti', 'Notifications')}</span>
                    {unreadCount > 0 ? (
                        <button type="button" style={{ ...btn(false), padding: '4px 10px', fontSize: 12 }}
                            onClick={() => void markNotificationsRead()}>{t('Procitaj sve', 'Read all')}</button>
                    ) : null}
                </div>
                {notifications.length === 0 ? (
                    <p style={{ color: '#8a95a5', fontSize: 13, textAlign: 'center', padding: 20 }}>
                        {t('Nema obavijesti.', 'No notifications.')}
                    </p>
                ) : (
                    notifications.slice(0, 30).map((n) => (
                        <div key={n.id} style={{
                            padding: '6px 8px', borderBottom: '1px solid #eee', fontSize: 13,
                            background: n.read_at ? 'transparent' : '#f0f6ff',
                            cursor: n.card_id ? 'pointer' : 'default',
                        }}
                        onClick={() => { if (n.card_id) void loadCard(n.card_id); setShowNotifs(false); }}>
                            <div style={{ color: '#536176' }}>
                                <span style={{ fontWeight: 600 }}>{n.actor_name || '?'}</span>{' '}
                                {n.type.replace(/_/g, ' ')}
                            </div>
                            <div style={{ fontSize: 12, color: '#8a95a5' }}>{fmtDate(n.created_at)}</div>
                        </div>
                    ))
                )}
            </div>
        );
    };

    // --- Main render ---

    return (
        <div className="t2-shell" style={embedded
            ? { background: '#f3f5f8', color: ink, display: 'flex', flexDirection: 'column', minHeight: '100vh', borderRadius: 12, overflow: 'hidden' }
            : { position: 'fixed', inset: 0, zIndex: 4000, background: '#f3f5f8', color: ink, display: 'flex', flexDirection: 'column', overflow: 'hidden' }
        }>
            <style>{`
                .t2-shell { font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; font-size: 14px; line-height: 1.45; }
                .t2-shell button, .t2-shell input, .t2-shell select, .t2-shell textarea { font: inherit; }
                .t2-shell button:focus-visible, .t2-shell input:focus-visible, .t2-shell select:focus-visible, .t2-shell textarea:focus-visible { outline: 3px solid rgba(155,107,22,.24); outline-offset: 2px; }
                .t2-shell *::-webkit-scrollbar { height: 9px; width: 9px; }
                .t2-shell *::-webkit-scrollbar-track { background: transparent; }
                .t2-shell *::-webkit-scrollbar-thumb { background: #c9d1da; border-radius: 5px; }
                .t2-shell *::-webkit-scrollbar-thumb:hover { background: #aab5c2; }

                @keyframes t2-fadein { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
                @keyframes t2-modal-in { from { opacity: 0; transform: translateY(14px) scale(.985); } to { opacity: 1; transform: none; } }
                @keyframes t2-shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }

                .t2-overlay { animation: t2-fadein .14s ease; }
                .t2-modal { animation: t2-modal-in .18s cubic-bezier(.2,.9,.3,1); }

                .t2-card { transition: box-shadow .14s ease, transform .14s ease, opacity .14s ease, border-color .14s ease; animation: t2-fadein .16s ease; }
                .t2-card:hover { box-shadow: 0 5px 16px rgba(23,32,51,.11); transform: translateY(-1px); border-color: #cfd6df; }
                .t2-card:active { cursor: grabbing; }
                .t2-card-dragging { opacity: .45 !important; transform: rotate(1.5deg) scale(.98); box-shadow: 0 12px 28px rgba(23,32,51,.2); }

                .t2-lane { transition: border-color .15s ease, background .15s ease; }
                .t2-lane-over { border-color: rgba(155,107,22,.45) !important; background: #ebeef3 !important; }

                .t2-addcard { transition: background .12s ease, color .12s ease; }
                .t2-addcard:hover { background: #e4e8ed !important; color: #3d4f65 !important; }
                .t2-addlist { transition: border-color .15s ease, color .15s ease, background .15s ease; }
                .t2-addlist:hover { border-color: #9b6b16 !important; color: #9b6b16 !important; background: rgba(155,107,22,.04) !important; }

                .t2-boardcard { transition: box-shadow .15s ease, transform .15s ease, border-color .15s ease; animation: t2-fadein .18s ease; }
                .t2-boardcard:hover { box-shadow: 0 8px 24px rgba(23,32,51,.1); transform: translateY(-2px); border-color: #b8c1ce; }

                .t2-row { transition: background .1s ease; }
                .t2-row:hover td { background: #f6f8fa; }
                .t2-myrow { transition: background .1s ease; }
                .t2-myrow:hover { background: #f6f8fa; }
                .t2-clitem .t2-clitem-x { opacity: 0; transition: opacity .12s ease; }
                .t2-clitem:hover .t2-clitem-x { opacity: 1; }
                .t2-clitem:hover { background: #f1f4f7; }

                .t2-skel { background: linear-gradient(90deg, #e6eaef 25%, #f2f4f7 50%, #e6eaef 75%); background-size: 800px 100%; animation: t2-shimmer 1.3s linear infinite; }

                @media (max-width: 799px) {
                    .t2-shell .t2-header { padding: 10px 14px !important; }
                    /* Mobile Kanban: one column per swipe, snap scrolling */
                    .t2-lanes-mobile { scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; }
                    .t2-lanes-mobile > * { scroll-snap-align: center; min-width: 84vw !important; width: 84vw !important; max-width: 84vw !important; }
                    .t2-card:hover { transform: none; }
                }
            `}</style>

            {/* Header */}
            <div className="t2-header" style={{
                position: 'relative',
                background: '#fff', borderBottom: '1px solid #dfe4ea',
                padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 12,
                boxShadow: '0 2px 10px rgba(23,32,51,.05)',
            }}>
                <button type="button" style={{ ...btn(false), padding: '6px 12px' }} onClick={onBack}>
                    {t('Izlaz', 'Exit')}
                </button>
                <span style={{ fontWeight: 700, fontSize: 17, color: ink }}>{t('Zadaci v2', 'Tasks v2')}</span>
                {/* Prekidac modula 11.1 / 11.2 / 11.3 */}
                {onOpenTeamTasks ? (
                    <button type="button" style={pillBtn(false)} title="m.11.1" onClick={onOpenTeamTasks}>
                        {t('Timski', 'Teams')}
                    </button>
                ) : null}
                {onOpenAsana ? (
                    <button type="button" style={pillBtn(false)} title="m.11.2" onClick={onOpenAsana}>
                        {t('Moja Asana', 'My Asana')}
                    </button>
                ) : null}
                <div style={{ flex: 1 }} />

                {/* View tabs */}
                <button type="button" style={pillBtn(view === 'boards')}
                    onClick={() => { setView('boards'); void loadBoards(); }}>
                    {t('Boardovi', 'Boards')}
                </button>
                <button type="button" style={pillBtn(view === 'my-cards')}
                    onClick={() => { setView('my-cards'); void loadMyCards(); }}>
                    {t('Moji zadaci', 'My tasks')}
                </button>

                {/* Push notifications toggle */}
                {'serviceWorker' in navigator && 'PushManager' in window ? (
                    <button type="button" style={{
                        ...btn(false), padding: '5px 12px', fontSize: 12,
                        color: pushEnabled ? '#27ae60' : '#8a95a5',
                        borderColor: pushEnabled ? '#27ae60' : '#cfd6df',
                    }}
                    onClick={() => { if (pushEnabled) { void unsubscribePush(); } else { void subscribePush(); } }}>
                        {pushEnabled ? t('Push ON', 'Push ON') : t('Push OFF', 'Push OFF')}
                    </button>
                ) : null}

                {/* Notifications bell */}
                <button type="button"
                    onClick={() => { setShowNotifs(!showNotifs); if (!showNotifs) void loadNotifications(); }}
                    style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', color: ink, padding: '4px 8px', display: 'flex', alignItems: 'center' }}>
                    <IconBell />
                    {unreadCount > 0 ? (
                        <span style={{
                            position: 'absolute', top: 0, right: 0, background: '#c0392b', color: '#fff',
                            borderRadius: '50%', width: 16, height: 16, fontSize: 11, fontWeight: 700,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
                    ) : null}
                </button>
                {renderNotifications()}
            </div>

            {/* Error bar */}
            {error ? (
                <div style={{ background: '#fdecea', color: '#b14040', padding: '8px 24px', fontSize: 14, display: 'flex', justifyContent: 'space-between' }}>
                    <span>{error}</span>
                    <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b14040', fontWeight: 700 }}
                        onClick={() => setError('')}>x</button>
                </div>
            ) : null}

            {/* Content */}
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {loading && view !== 'board' ? renderSkeleton() : (
                    <>
                        {view === 'boards' ? renderBoards() : null}
                        {view === 'board' ? renderBoard() : null}
                        {view === 'my-cards' ? renderMyCards() : null}
                    </>
                )}
            </div>

            {/* Modals */}
            {renderCardDetail()}
            {renderMembersPanel()}
            {renderLabelForm()}
            {renderBoardSettings()}
        </div>
    );
}

// --- Sub-components ---

function CardEditForm({
    card, lists, allUsers, locale, onSave, onCancel,
}: {
    card: CardSummary;
    lists: ListType[];
    allUsers: { id: number; name: string; email: string }[];
    locale: Locale;
    onSave: (data: Record<string, unknown>) => void;
    onCancel: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [title, setTitle] = useState(card.title);
    const [description, setDescription] = useState(card.description || '');
    const [assignee, setAssignee] = useState(card.assignee_user_id?.toString() || '');
    const [priority, setPriority] = useState(card.priority);
    const [dueDate, setDueDate] = useState(card.due_date || '');
    const [startDate, setStartDate] = useState(card.start_date || '');
    const [listId, setListId] = useState(card.list_id?.toString() || '');

    const submit = () => {
        onSave({
            title: title.trim(),
            description: description.trim() || null,
            assignee_user_id: assignee ? Number(assignee) : null,
            priority,
            due_date: dueDate || null,
            start_date: startDate || null,
            list_id: listId ? Number(listId) : null,
        });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
                style={field} placeholder={t('Naslov', 'Title')} />
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                style={{ ...field, minHeight: 80, resize: 'vertical' }}
                placeholder={t('Opis', 'Description')} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <select value={assignee} onChange={(e) => setAssignee(e.target.value)}
                    style={{ ...field, flex: 1, minWidth: 150 }}>
                    <option value="">{t('Nedodijeljeno', 'Unassigned')}</option>
                    {allUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
                <select value={priority} onChange={(e) => setPriority(e.target.value)}
                    style={{ ...field, width: 120, flex: 'none' }}>
                    <option value="low">{t('Nizak', 'Low')}</option>
                    <option value="normal">{t('Normalan', 'Normal')}</option>
                    <option value="high">{t('Visok', 'High')}</option>
                    <option value="urgent">{t('Hitan', 'Urgent')}</option>
                </select>
                <select value={listId} onChange={(e) => setListId(e.target.value)}
                    style={{ ...field, width: 150, flex: 'none' }}>
                    <option value="">{t('Bez liste', 'No list')}</option>
                    {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, color: '#536176' }}>{t('Pocetak', 'Start')}</label>
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={field} />
                </div>
                <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, color: '#536176' }}>{t('Rok', 'Due')}</label>
                    <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={field} />
                </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button type="button" style={btn()} onClick={submit}>{t('Spremi', 'Save')}</button>
                <button type="button" style={btn(false)} onClick={onCancel}>{t('Odustani', 'Cancel')}</button>
            </div>
        </div>
    );
}

function ChecklistItemAdder({ onAdd, locale }: { onAdd: (text: string) => void; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [text, setText] = useState('');
    return (
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <input value={text} onChange={(e) => setText(e.target.value)}
                placeholder={t('Novi element...', 'New item...')}
                style={{ padding: '5px 8px', border: '1px solid #cfd6df', borderRadius: 6, fontSize: 13, flex: 1 }}
                onKeyDown={(e) => { if (e.key === 'Enter' && text.trim()) { onAdd(text.trim()); setText(''); } }} />
            <button type="button" style={{ ...btn(), padding: '5px 10px', fontSize: 12 }}
                onClick={() => { if (text.trim()) { onAdd(text.trim()); setText(''); } }}>{t('Dodaj', 'Add')}</button>
        </div>
    );
}
